/**
 * CheckoutModal — create a Chargily (test-mode) or SIMULATED checkout for a lab's subscription to a plan
 * (MANAGEMENT 14 billing). POSTs /management/billing/labs/:id/checkout, which writes a pending
 * SubscriptionPayment then returns a checkout URL — a REAL Chargily test checkout when CHARGILY_SECRET_KEY
 * is set on the server, else a SIMULATED url (the server owns that gate). The operator picks the target lab
 * id + the billing cycle; the modal previews the amount from the plan's tariff and, on success, surfaces the
 * checkout URL (opening it in a new tab) with a badge showing whether it was simulated or a Chargily test.
 *
 * When a lab id is pre-supplied (from the billing tab) it seeds + locks the field. AA: labelled fields,
 * role="alert" error, a `rel="noopener"` external link. FR i18n; amounts/ids ride `.nv-ltr-island`.
 */
import { useState } from "react";
import { Badge, Button, FormField, Input, Modal, Select, useToast } from "@/ui";
import { ApiError } from "@/api";
import { useT } from "@/app";
import { formatCentimes } from "../screens/pricing.lib";
import { useCreateCheckout } from "../screens/pricing.api";
import { BILLING_CYCLES, type BillingCycle, type CheckoutResult, type PlanRow } from "../screens/pricing.types";

export interface CheckoutModalProps {
  plan: PlanRow | undefined;
  /** Optional pre-supplied lab id (from the billing tab) — seeds + locks the field when present. */
  presetLabId?: string | undefined;
  onClose: () => void;
}

export function CheckoutModal({ plan, presetLabId, onClose }: CheckoutModalProps) {
  const { t } = useT();
  const toast = useToast();
  const open = plan !== undefined;

  const [labId, setLabId] = useState("");
  const [cycle, setCycle] = useState<BillingCycle>("mensuel");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CheckoutResult | null>(null);

  const checkout = useCreateCheckout(labId.trim() || "0");

  const seedKey = open ? `${plan!.code}:${presetLabId ?? ""}` : "_closed";
  const [seeded, setSeeded] = useState<string | null>(null);
  if (open && seeded !== seedKey) {
    setSeeded(seedKey);
    setLabId(presetLabId ?? "");
    setCycle("mensuel");
    setError(null);
    setResult(null);
  }
  if (!open && seeded !== null) setSeeded(null);

  // The previewed amount tracks the selected cycle (annual falls back to a "no annual tariff" note).
  const amountCentimes =
    plan == null ? null : cycle === "annuel" ? plan.prixAnnuelCentimes : plan.prixMensuelCentimes;

  const submit = () => {
    setError(null);
    if (!/^\d+$/.test(labId.trim())) {
      setError(t("mgmt.pricing.assign.labIdRequired"));
      return;
    }
    checkout.mutate(
      { plan_code: plan!.code, cycle },
      {
        onSuccess: (data) => {
          setResult(data);
          toast.success(t("mgmt.pricing.checkout.created"));
        },
        onError: (err) => setError(err instanceof ApiError ? err.message : t("mgmt.pricing.error")),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={open ? t("mgmt.pricing.checkout.title", { plan: plan!.nom }) : ""}
      footer={
        result ? (
          <Button onClick={onClose}>{t("mgmt.pricing.checkout.close")}</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t("mgmt.pricing.cancel")}
            </Button>
            <Button onClick={submit} loading={checkout.isPending}>
              {t("mgmt.pricing.checkout.submit")}
            </Button>
          </>
        )
      }
    >
      {result ? (
        // ── Success view: the generated checkout link + the simulated/live badge ──
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--nv-s-2)" }}>
            <span style={{ fontWeight: 600 }}>{t("mgmt.pricing.checkout.urlTitle")}</span>
            <Badge tone={result.simulated ? "warn" : "info"}>
              {result.simulated ? t("mgmt.pricing.checkout.simulatedBadge") : t("mgmt.pricing.checkout.liveBadge")}
            </Badge>
          </div>
          <div
            className="nv-ltr-island"
            style={{
              fontFamily: "var(--nv-font-mono)",
              fontSize: "var(--nv-fs-caption)",
              color: "var(--nv-text-2, var(--nv-slate))",
              wordBreak: "break-all",
              padding: "var(--nv-s-2)",
              background: "var(--nv-surface-2)",
              borderRadius: "var(--nv-r-sm)",
            }}
          >
            {result.checkoutUrl ?? "—"}
          </div>
          {result.checkoutUrl && (
            // Opens the checkout in a new tab (noopener). A button (not a bare <a>) keeps the token styling
            // + the ≥44px touch target; the destination is server-issued (Chargily test or the simulated url).
            <Button onClick={() => window.open(result.checkoutUrl!, "_blank", "noopener,noreferrer")}>
              {t("mgmt.pricing.checkout.open")}
            </Button>
          )}
          {result.simulated && (
            <p style={{ margin: 0, fontSize: "var(--nv-fs-caption)", color: "var(--nv-text-3, var(--nv-steel))" }}>
              {t("mgmt.pricing.checkout.simulatedNote")}
            </p>
          )}
        </div>
      ) : (
        // ── Form view ──
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
          <FormField label={t("mgmt.pricing.checkout.labId")} required>
            {(p) => (
              <Input
                {...p}
                value={labId}
                onChange={(e) => setLabId(e.target.value)}
                disabled={!!presetLabId}
                inputMode="numeric"
                className="nv-ltr-island"
              />
            )}
          </FormField>
          <FormField label={t("mgmt.pricing.checkout.cycle")} required>
            {(p) => (
              <Select
                {...p}
                value={cycle}
                onChange={(e) => setCycle(e.target.value as BillingCycle)}
                options={BILLING_CYCLES.map((c) => ({ value: c, label: t(`mgmt.pricing.checkout.cycle.${c}`) }))}
              />
            )}
          </FormField>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-1)" }}>
            <span style={{ fontSize: "var(--nv-fs-label)", color: "var(--nv-text-2, var(--nv-slate))" }}>
              {t("mgmt.pricing.checkout.amount")}
            </span>
            <span
              className="nv-ltr-island"
              style={{ fontFamily: "var(--nv-font-mono)", fontWeight: 700, fontSize: "var(--nv-fs-h3)" }}
            >
              {amountCentimes != null ? formatCentimes(amountCentimes) : t("mgmt.pricing.noAnnual")}
            </span>
          </div>
          <p style={{ margin: 0, fontSize: "var(--nv-fs-caption)", color: "var(--nv-text-3, var(--nv-steel))" }}>
            {t("mgmt.pricing.checkout.simulatedNote")}
          </p>
          {error && (
            <p role="alert" style={{ margin: 0, color: "var(--nv-danger, var(--nv-critical))" }}>
              {error}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
